"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User } from "@/context/user-context";
import { formatRelativeTime, imagePath } from "@/lib/userUtils";
import { Enum_Comment_Commentstatus } from "@/types/generated";
import { Reply } from "lucide-react";
import { z } from "zod";
import { Comment, CommentForm, formSchema } from "./commentForm";

// Comment Item Component
export interface CommentItemProps {
  comment: Comment;
  token: string | null;
  user: User;
  onReply: (commentId: string) => void;
  onSubmitReply: (data: z.infer<typeof formSchema>) => Promise<void>;
  replyingTo: string | null;
  cancelReply: () => void;
  level: number;
}

export function CommentItem({
  comment,
  token,
  user,
  onReply,
  onSubmitReply,
  replyingTo,
  cancelReply,
  level,
}: CommentItemProps) {
  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const showComment =
    comment.commentStatus === Enum_Comment_Commentstatus.Published ||
    (token && user?.documentId === comment?.approvedBy?.documentId);

  if (!showComment) return null;

  return (
    <div className={`flex gap-4 py-2`}>
      <Avatar className="h-10 w-10">
        <AvatarImage src={imagePath(comment.approvedBy?.avatar?.url)} />
        <AvatarFallback>
          {comment.approvedBy?.name?.charAt(0).toUpperCase() || "A"}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 overflow-x-hidden">
        <div className="rounded-lg bg-neutral-100 dark:bg-neutral-900 p-4 hover:bg-neutral-200 transition-colors w-full whitespace-pre-wrap break-words">
          <div className="flex items-center gap-1">
            <p className="font-semibold text-sm">{comment.approvedBy?.name}</p>

            {comment?.commentBy?.role?.type !== "authenticated" && (
              <>
                <span className="">•</span>
                <span className="text-sm text-neutral-500">
                  {comment?.commentBy?.role?.name}
                </span>
              </>
            )}
          </div>

          <p className="text-base text-neutral-800 dark:text-neutral-200 pt-2">{comment.content}</p>
        </div>
        {/* {comment.approved === false && (
          <div className="mt-1 text-xs text-yellow-600 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Waiting for approval</span>
          </div>
        )} */}
        <div className="flex gap-2 mt-1 ms-1 items-center">
          {level !== 3 && token && (
            <>
              <span
                role="button"
                onClick={() => onReply(comment.documentId)}
                className=" text-xs font-medium text-neutral-600 hover:text-blue-600 cursor-pointer"
              >
                Reply
              </span>
              <Reply className="h-4 w-4" />
              <span className="">•</span>
            </>
          )}

          <span className="text-xs text-neutral-500 dark:text-neutral-300">
            {formatRelativeTime(comment.createdAt)}
          </span>
        </div>
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-4 space-y-2">
            {comment.replies.map((reply) => (
              <CommentItem
                level={level + 1}
                key={reply.documentId}
                comment={reply}
                token={token}
                user={user}
                onReply={onReply}
                onSubmitReply={onSubmitReply}
                replyingTo={replyingTo}
                cancelReply={cancelReply}
              />
            ))}
          </div>
        )}

        {level !== 3 && replyingTo === comment.documentId && (
          <div className="mt-2">
            <CommentForm
              articleId={comment.documentId}
              parentCommentId={comment.documentId}
              user={user}
              token={token}
              onSubmit={onSubmitReply}
              onCancel={cancelReply}
              isReply
            />
          </div>
        )}
      </div>
    </div>
  );
}
